
import { Home, Calendar, MessageSquare, Target } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useWorkout } from "@/contexts/WorkoutContext";
import { useAuth } from "@/hooks/useAuth";

export type ClientNavTab = "home" | "plan" | "explore" | "chat";

type NavItem = {
  icon?: React.ElementType;
  emoji?: string;
  label: string;
  href: string;
  isActive?: boolean;
  isPrimary?: boolean;
};

export function getActiveTabFromPath(pathname: string): ClientNavTab {
  if (pathname.startsWith("/client-plan")) return "plan";
  if (
    pathname.startsWith("/explore") ||
    pathname.startsWith("/lesson/") ||
    pathname.startsWith("/assessments") ||
    pathname.startsWith("/view-workout/")
  ) {
    return "explore";
  }
  if (pathname.startsWith("/chat")) return "chat";
  return "home";
}

export function MobileNavbar({
  activeTab = "home"
}: {
  activeTab?: ClientNavTab;
}) {
  const isMobile = useIsMobile();
  const { isWorkoutActive, returnToWorkout } = useWorkout();
  const { profile, isClient } = useAuth();
  
  const isBlocked = isClient && profile?.legal_document_approval_needed;
  const legalRedirectTarget = "/client-profile?tab=legal";
  
  const navItems: NavItem[] = [{
    icon: Home,
    label: "Home",
    href: "/client-portal",
    isActive: activeTab === "home"
  }, {
    icon: Calendar,
    label: "Plan",
    href: "/client-plan",
    isActive: activeTab === "plan"
  }, {
    emoji: "🏋️",
    label: "Workout",
    href: isWorkoutActive ? "#" : "/start-workout",
    isPrimary: true
  }, {
    icon: Target,
    label: "Explore",
    href: "/explore",
    isActive: activeTab === "explore"
  }, {
    icon: MessageSquare,
    label: "Chat",
    href: "/chat",
    isActive: activeTab === "chat"
  }];
  
  if (!isMobile) return null;
  
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 overflow-visible bg-card border-t border-border pb-safe-bottom">
      <div
        className="flex items-center justify-between px-1 overflow-visible"
        style={{ minHeight: "var(--client-nav-height)" }}
      >
        {navItems.map(item => (
          <NavButton 
            key={item.emoji || item.href} 
            item={item}
            onPrimaryClick={item.isPrimary && isWorkoutActive ? returnToWorkout : undefined}
            isBlocked={isBlocked}
            legalRedirectTarget={legalRedirectTarget}
          />
        ))}
      </div>
    </div>
  );
}

function NavButton({
  item,
  onPrimaryClick,
  isBlocked,
  legalRedirectTarget
}: {
  item: NavItem;
  onPrimaryClick?: () => void;
  isBlocked?: boolean;
  legalRedirectTarget: string;
}) {
  if (item.isPrimary) {
    const blockedDestination = legalRedirectTarget;
    const primaryButton = (
      <Button 
        className={cn(
          "rounded-full shadow-neon-glow",
          "flex items-center justify-center border-4 border-background px-0",
          "!h-[var(--client-nav-fab-height)] !w-[var(--client-nav-fab-width)] !min-w-[var(--client-nav-fab-width)]",
          "-mt-client-nav-fab",
          isBlocked 
            ? "bg-gray-600 text-gray-400 cursor-not-allowed opacity-50" 
            : "gradient-primary text-black",
          onPrimaryClick ? "animate-pulse-glow" : ""
        )}
        disabled={isBlocked}
        title={isBlocked ? "Legal document approval required" : item.label}
        aria-label={item.label}
      >
        <span className="text-4xl leading-none">{item.emoji}</span>
      </Button>
    );
    
    if (onPrimaryClick) {
      return (
        <div className="flex flex-1 items-center justify-center overflow-visible" onClick={isBlocked ? undefined : onPrimaryClick}>
          {primaryButton}
        </div>
      );
    }
    
    if (isBlocked) {
      return (
        <Link to={blockedDestination} className="flex flex-1 items-center justify-center overflow-visible">
          {primaryButton}
        </Link>
      );
    }
    
    return (
      <Link to={item.href} className="flex flex-1 items-center justify-center overflow-visible">
        {primaryButton}
      </Link>
    );
  }
  
  const inactiveNavClass = "text-white/80 hover:text-white";
  const activeNavClass = "text-primary";

  const navContent = (
    <>
      {item.icon && (
        <item.icon
          size={30}
          strokeWidth={item.isActive ? 2.5 : 2}
          className="shrink-0"
        />
      )}
      <span className={cn(
        "text-[11px] font-medium leading-none mt-1",
        item.isActive ? activeNavClass : inactiveNavClass
      )}>
        {item.label}
      </span>
    </>
  );

  if (isBlocked) {
    return (
      <Button 
        asChild
        variant="ghost" 
        className={cn(
          "flex flex-1 flex-col items-center justify-center rounded-none h-16 py-1.5",
          inactiveNavClass,
          "[&_svg]:!size-[30px]"
        )}
        title="Legal document approval required"
      >
        <Link to={legalRedirectTarget} className="flex flex-col items-center justify-center h-full w-full">
          {navContent}
        </Link>
      </Button>
    );
  }
  
  return (
    <Button
      asChild
      variant="ghost"
      className={cn(
        "flex flex-1 flex-col items-center justify-center rounded-none h-16 py-1.5",
        "[&_svg]:!size-[30px]",
        item.isActive ? activeNavClass : inactiveNavClass
      )}
    >
      <Link to={item.href} className="flex flex-col items-center justify-center h-full w-full">
        {navContent}
      </Link>
    </Button>
  );
}
